import { useController, useForm } from 'react-hook-form';
import { useAddressValidation } from './useAddressValidation';
/**
 * Custom hook for managing address form state management and validation.
 * @param props - Optional form configuration props
 */
export function useAddressForm({ shouldBlockExternalTerritoryPostcodes, ...rest }) {
    const { validatePostcode, validateParcelLocker, validateStateBelongsToPostcode, validatePostcodeNotBlockedFromDelivery, } = useAddressValidation();
    const { control, ...otherForm } = useForm({
        ...rest,
        defaultValues: {
            addressLine1: '',
            addressLine2: '',
            postcode: '',
            state: '',
            suburb: '',
            ...(rest || {}).defaultValues,
        },
    });
    const addressLine1ValidationMessage = validateParcelLocker(otherForm.getValues().addressLine1);
    const addressLine2ValidationMessage = validateParcelLocker(otherForm.getValues().addressLine2);
    const controlAddressLine1 = useController({
        name: 'addressLine1',
        control,
        rules: {
            required: true,
            validate: (value) => validateParcelLocker(value)?.errorMessage ?? true,
        },
    });
    const controlAddressLine2 = useController({
        name: 'addressLine2',
        control,
        rules: {
            validate: (value) => {
                if (!value) {
                    return true;
                }
                return validateParcelLocker(value)?.errorMessage ?? true;
            },
        },
    });
    const controlSuburb = useController({
        name: 'suburb',
        control,
        rules: {
            required: true,
        },
    });
    const controlPostcode = useController({
        name: 'postcode',
        control,
        rules: {
            required: true,
            validate: (value) => {
                // First validate the postcode format
                const formatValidation = validatePostcode(value);
                if (!formatValidation.isValid) {
                    return formatValidation.errorMessage;
                }
                if (shouldBlockExternalTerritoryPostcodes) {
                    // check for blocked postcodes
                    const blockedValidation = validatePostcodeNotBlockedFromDelivery(value);
                    if (!blockedValidation.isValid) {
                        return blockedValidation.errorMessage;
                    }
                }
                // check state match
                const stateValidation = validateStateBelongsToPostcode(value, otherForm.getValues().state);
                if (!stateValidation.isValid) {
                    return stateValidation.errorMessage;
                }
                return true;
            },
        },
    });
    const controlState = useController({
        name: 'state',
        control,
        rules: {
            required: true,
        },
    });
    const defaultControllers = {
        controlAddressLine1,
        controlAddressLine2,
        controlSuburb,
        controlPostcode,
        controlState,
    };
    return {
        ...otherForm,
        control,
        defaultControllers,
        additionalErrorStates: {
            addressLine1ValidationMessage,
            addressLine2ValidationMessage,
        },
    };
}
//# sourceMappingURL=data:application/json;base64,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